import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ScatterChart, Scatter, AreaChart, Area } from 'recharts';
import { Plus, X, Save, Eye, Download, Grid3X3, BarChart3, PieChart as PieChartIcon, TrendingUp, Filter, ArrowUp, ArrowDown, MoreVertical, Copy, Share2 } from 'lucide-react';
import { apiCallJson, apiCall } from './Api';
import SchedulerWidget from './SchedulerWidget';

const OLAPCubeBuilder = ({ user }) => {
  const [cubeConfig, setCubeConfig] = useState({
    name: 'New Cube',
    dimensions: [],
    measures: [],
    chartType: 'bar',
    sortBy: '',
    sortOrder: 'desc',
    limit: 100
  });
  
  const [availableDimensions, setAvailableDimensions] = useState([]);
  const [availableMeasures, setAvailableMeasures] = useState([]);
  const [savedCubes, setSavedCubes] = useState([]);
  const [queryResult, setQueryResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('builder');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [drillDownPath, setDrillDownPath] = useState([]);
  
  const [olapOperations, setOlapOperations] = useState({
    rollup: false,
    slice: [],
    dice: [],
    pivot: { rows: [], columns: [], values: [] }
  });

  // Add pivot-specific state
  const [pivotConfig, setPivotConfig] = useState({
    name: 'New Pivot Table',
    rows: [],
    columns: [],
    values: [],
    filters: []
  });
  const [savedPivots, setSavedPivots] = useState([]);
  const [showSavePivotDialog, setShowSavePivotDialog] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [pivotData, setPivotData] = useState(null);
  
  // Track currently loaded cube/pivot IDs
  const [currentCubeId, setCurrentCubeId] = useState(null);
  const [currentPivotId, setCurrentPivotId] = useState(null);

  // Load saved pivots
  const loadSavedPivots = async () => {
    try {
      const pivots = await apiCallJson('/api/olap/pivots');
      setSavedPivots(pivots);
    } catch (error) {
      console.error('Failed to load pivots:', error);
    }
  };

  // Handle URL query parameters to auto-load cube or pivot
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const cubeId = urlParams.get('cube_id');
    const pivotId = urlParams.get('pivot_id');

    if (cubeId && savedCubes.length > 0) {
      const cube = savedCubes.find(c => c.id == cubeId);
      if (cube) {
        loadCube(cube);
      }
    } else if (pivotId && savedPivots.length > 0) {
      const pivot = savedPivots.find(p => p.id == pivotId);
      if (pivot) {
        loadPivot(pivot);
      }
    }
  }, [savedCubes, savedPivots]);

  // Auto-execute when cube config is loaded from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const cubeId = urlParams.get('cube_id');
    
    if (cubeId && currentCubeId == cubeId && 
        cubeConfig.dimensions.length > 0 && cubeConfig.measures.length > 0 &&
        !queryResult) {
      executeQuery();
    }
  }, [cubeConfig, currentCubeId]);

  // Auto-execute when pivot config is loaded from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const pivotId = urlParams.get('pivot_id');
    
    if (pivotId && currentPivotId == pivotId && 
        pivotConfig.rows.length > 0 && pivotConfig.values.length > 0 &&
        !pivotData) {
      generatePivotTable();
    }
  }, [pivotConfig, currentPivotId]);

  // Save pivot configuration
  const savePivot = async () => {
    if (!pivotConfig.name.trim()) {
      alert('Please enter a pivot name');
      return;
    }

    try {
      const pivotData = {
        pivot_name: pivotConfig.name,
        pivot_config: {
          rows: pivotConfig.rows,
          columns: pivotConfig.columns,
          values: pivotConfig.values,
          filters: pivotConfig.filters,
          baseFilters: filterConfig
        },
        is_shared: false
      };

      await apiCall('/api/olap/pivots', {
          method: 'POST', 
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify(pivotData)
      });
      await loadSavedPivots();
      setShowSavePivotDialog(false);
      alert('Pivot saved successfully');
    } catch (error) {
      console.error('Failed to save pivot:', error);
      alert('Failed to save pivot: ' + error.message);
    }
  };

  // Load pivot configuration
  const loadPivot = (pivot) => {
    const config = pivot.pivot_config;
    setPivotConfig({
      name: pivot.pivot_name,
      rows: config.rows || [],
      columns: config.columns || [],
      values: config.values || [],
      filters: config.filters || []
    });
    if (config.baseFilters) {
      setFilterConfig(config.baseFilters);
    }
    setActiveTab('results'); // Changed from 'pivot' to show in Results tab
    
    // Clear cube query result when loading a pivot
    setQueryResult(null);
    
    // Track current pivot ID and update URL
    setCurrentPivotId(pivot.id);
    setCurrentCubeId(null);
    const newUrl = new URL(window.location);
    newUrl.searchParams.set('pivot_id', pivot.id);
    newUrl.searchParams.delete('cube_id');
    window.history.pushState({}, '', newUrl);
  };

  // Drag and drop handlers
  const handleDragStart = (e, item, type) => {
    setDraggedItem({ ...item, sourceType: type });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetZone) => {
    e.preventDefault();
    if (!draggedItem) return;

    const newItem = { ...draggedItem };
    delete newItem.sourceType;

    // Remove from source if it was already in pivot config
    setPivotConfig(prev => {
      const newConfig = { ...prev };
      
      // Remove from all zones first
      newConfig.rows = newConfig.rows.filter(item => item.id !== draggedItem.id);
      newConfig.columns = newConfig.columns.filter(item => item.id !== draggedItem.id);
      newConfig.values = newConfig.values.filter(item => item.id !== draggedItem.id);
      newConfig.filters = newConfig.filters.filter(item => item.id !== draggedItem.id);

      // Add to target zone
      if (targetZone === 'rows' && (draggedItem.type !== 'number' || availableDimensions.find(d => d.id === draggedItem.id))) {
        newConfig.rows.push(newItem);
      } else if (targetZone === 'columns' && (draggedItem.type !== 'number' || availableDimensions.find(d => d.id === draggedItem.id))) {
        newConfig.columns.push(newItem);
      } else if (targetZone === 'values' && availableMeasures.find(m => m.id === draggedItem.id)) {
        newConfig.values.push(newItem);
      } else if (targetZone === 'filters') {
        newConfig.filters.push(newItem);
      }

      return newConfig;
    });

    setDraggedItem(null);
  };

  const removeFromPivot = (itemId, zone) => {
    setPivotConfig(prev => ({
      ...prev,
      [zone]: prev[zone].filter(item => item.id !== itemId)
    }));
  };

  // Generate shareable URL for cube or pivot
  const generateShareUrl = (type, id) => {
    const baseUrl = window.location.origin + window.location.pathname;
    const param = type === 'cube' ? 'cube_id' : 'pivot_id';
    return `${baseUrl}?${param}=${id}`;
  };

  // Copy share URL to clipboard
  const copyShareUrl = (type, id, name) => {
    const url = generateShareUrl(type, id);
    navigator.clipboard.writeText(url).then(() => {
      alert(`Share link copied for ${name}!\n${url}`);
    }).catch(err => {
      console.error('Failed to copy URL:', err);
      alert('Failed to copy URL to clipboard');
    });
  };

  // Generate pivot table
  const generatePivotTable = async () => {
    if (pivotConfig.rows.length === 0 || pivotConfig.values.length === 0) {
      alert('Please add at least one row dimension and one value measure');
      return;
    }

    setLoading(true);
    try {
      const queryData = {
        dimensions: [...pivotConfig.rows.map(r => r.id), ...pivotConfig.columns.map(c => c.id)],
        measures: pivotConfig.values.map(v => v.id),
        filters: filterConfig,
        olapOperations: {
          ...olapOperations,
          pivot: {
            rows: pivotConfig.rows,
            columns: pivotConfig.columns,
            values: pivotConfig.values
          }
        },
        sortBy: '',
        sortOrder: 'desc',
        limit: 5000, // Higher limit for pivot tables
        host_tenant_id: user.host_tenant_id
      };

      const result = await apiCallJson('/api/olap/pivot', {
          method: 'POST', 
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify(queryData)
      });
      
      setPivotData(result);
      setActiveTab('results'); // Show pivot results in Results tab
      
      // Keep pivot_id in URL when executing
      if (currentPivotId) {
        const newUrl = new URL(window.location);
        if (!newUrl.searchParams.has('pivot_id')) {
          newUrl.searchParams.set('pivot_id', currentPivotId);
          window.history.pushState({}, '', newUrl);
        }
      }
    } catch (error) {
      console.error('Pivot generation failed:', error);
      alert('Pivot generation failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Enhanced pivot table renderer - proper cross-tabulation with hierarchical column headers
  const renderEnhancedPivotTable = () => {
    if (!pivotData || !pivotData.data) return null;

    const { rows, columns, values } = pivotConfig;
    const data = pivotData.data;

    // No columns defined - simple aggregation table
    if (columns.length === 0) {
      return renderSimplePivotTable(data, rows, values);
    }

    // ===== Build proper pivot structure =====

    // 1. Collect unique values for each column dimension (preserve order of appearance)
    const colDimValues = columns.map(col => {
      const seen = new Set();
      const ordered = [];
      data.forEach(row => {
        const v = row[col.id];
        const key = v !== null && v !== undefined ? String(v) : '';
        if (!seen.has(key)) {
          seen.add(key);
          ordered.push(key);
        }
      });
      return ordered;
    });

    // 2. Build leaf column combos (cartesian of all column dimension values that actually exist in data)
    const existingCombos = new Set();
    data.forEach(row => {
      const combo = columns.map(c => {
        const v = row[c.id];
        return v !== null && v !== undefined ? String(v) : '';
      }).join('\x00');
      existingCombos.add(combo);
    });

    // Generate ordered leaf combos preserving dimension value order
    const leafCombos = [];
    const buildCombos = (depth, current) => {
      if (depth === columns.length) {
        const key = current.join('\x00');
        if (existingCombos.has(key)) {
          leafCombos.push([...current]);
        }
        return;
      }
      colDimValues[depth].forEach(val => {
        current.push(val);
        buildCombos(depth + 1, current);
        current.pop();
      });
    };
    buildCombos(0, []);

    // 3. Build data lookup:  rowKey -> comboKey -> { measureId: value }
    const pivotMap = {};
    const rowKeyParts = {}; // rowKey -> [part1, part2, ...]
    
    data.forEach(row => {
      const rParts = rows.map(r => {
        const v = row[r.id];
        return v !== null && v !== undefined ? String(v) : '';
      });
      const rowKey = rParts.join('\x00');
      const comboKey = columns.map(c => {
        const v = row[c.id];
        return v !== null && v !== undefined ? String(v) : '';
      }).join('\x00');

      if (!pivotMap[rowKey]) {
        pivotMap[rowKey] = {};
        rowKeyParts[rowKey] = rParts;
      }
      if (!pivotMap[rowKey][comboKey]) {
        pivotMap[rowKey][comboKey] = {};
      }
      values.forEach(v => {
        const existing = pivotMap[rowKey][comboKey][v.id];
        const newVal = row[v.id] || 0;
        pivotMap[rowKey][comboKey][v.id] = (existing || 0) + newVal;
      });
    });

    const sortedRowKeys = Object.keys(pivotMap).sort();
    const numValues = values.length;
    const colsPerLeaf = numValues; // each leaf combo gets one sub-column per measure

    // 4. Build hierarchical header rows
    const headerRows = [];

    if (columns.length === 1) {
      // Single column dimension: one header row with each distinct value spanning measures
      const headerCells = [];
      leafCombos.forEach(combo => {
        headerCells.push({
          label: combo[0] || '(empty)',
          colSpan: colsPerLeaf,
          className: 'bg-indigo-50 text-indigo-900 border border-gray-300 px-3 py-2 text-center font-semibold text-xs'
        });
      });
      // Grand total
      headerCells.push({
        label: 'Grand Total',
        colSpan: colsPerLeaf,
        className: 'bg-blue-100 text-blue-900 border border-gray-300 px-3 py-2 text-center font-semibold text-xs'
      });
      headerRows.push(headerCells);
    } else {
      // Multiple column dimensions: build one header row per dimension level
      for (let level = 0; level < columns.length; level++) {
        const headerCells = [];
        let prevGroup = null;
        let groupSpan = 0;

        leafCombos.forEach((combo, idx) => {
          // Group key is the values from level 0..level joined
          const groupKey = combo.slice(0, level + 1).join('\x00');
          
          if (groupKey === prevGroup) {
            groupSpan += colsPerLeaf;
          } else {
            if (prevGroup !== null) {
              headerCells.push({
                label: prevGroup.split('\x00')[level] || '(empty)',
                colSpan: groupSpan,
                className: level === 0
                  ? 'bg-indigo-50 text-indigo-900 border border-gray-300 px-3 py-2 text-center font-semibold text-xs'
                  : 'bg-purple-50 text-purple-900 border border-gray-300 px-3 py-2 text-center font-medium text-xs'
              });
            }
            prevGroup = groupKey;
            groupSpan = colsPerLeaf;
          }

          // Flush last group
          if (idx === leafCombos.length - 1) {
            headerCells.push({
              label: prevGroup.split('\x00')[level] || '(empty)',
              colSpan: groupSpan,
              className: level === 0
                ? 'bg-indigo-50 text-indigo-900 border border-gray-300 px-3 py-2 text-center font-semibold text-xs'
                : 'bg-purple-50 text-purple-900 border border-gray-300 px-3 py-2 text-center font-medium text-xs'
            });
          }
        });

        // Grand total on top level only
        if (level === 0) {
          headerCells.push({
            label: 'Grand Total',
            colSpan: colsPerLeaf,
            rowSpan: columns.length,
            className: 'bg-blue-100 text-blue-900 border border-gray-300 px-3 py-2 text-center font-semibold text-xs'
          });
        }

        headerRows.push(headerCells);
      }
    }

    // 5. Measure sub-header row (if multiple measures)
    const showMeasureHeader = numValues > 1;

    return (
      <div className="overflow-auto max-h-[600px] border border-gray-200 rounded-lg">
        <table className="border-collapse text-sm" style={{ minWidth: '100%' }}>
          <thead className="sticky top-0 z-10">
            {/* Hierarchical column dimension headers */}
            {headerRows.map((headerCells, rowIdx) => (
              <tr key={`hdr-${rowIdx}`}>
                {rowIdx === 0 && (
                  <th
                    className="bg-gray-100 border border-gray-300 px-3 py-2 text-left font-semibold text-xs sticky left-0 z-20"
                    rowSpan={headerRows.length + (showMeasureHeader ? 1 : 0)}
                    colSpan={rows.length}
                    style={{ minWidth: rows.length * 120 }}
                  >
                    {rows.map(r => r.name).join(' / ')}
                  </th>
                )}
                {headerCells.map((cell, cellIdx) => (
                  <th
                    key={`hdr-${rowIdx}-${cellIdx}`}
                    colSpan={cell.colSpan}
                    rowSpan={cell.rowSpan || 1}
                    className={cell.className}
                  >
                    {cell.label}
                  </th>
                ))}
              </tr>
            ))}

            {/* Measure sub-headers */}
            {showMeasureHeader && (
              <tr>
                {leafCombos.map((combo, comboIdx) => (
                  values.map((val, valIdx) => (
                    <th
                      key={`mhdr-${comboIdx}-${valIdx}`}
                      className="bg-gray-50 border border-gray-300 px-2 py-1 text-center font-medium text-xs text-gray-600"
                    >
                      {val.name}
                    </th>
                  ))
                ))}
                {/* Grand total measure headers */}
                {values.map((val, valIdx) => (
                  <th
                    key={`mhdr-gt-${valIdx}`}
                    className="bg-blue-50 border border-gray-300 px-2 py-1 text-center font-medium text-xs text-blue-700"
                  >
                    {val.name}
                  </th>
                ))}
              </tr>
            )}

            {/* Single measure: no sub-header needed, but add a thin spacer if only 1 value  */}
            {!showMeasureHeader && numValues === 1 && headerRows.length > 0 && null}
          </thead>

          <tbody>
            {sortedRowKeys.map((rowKey, rowIdx) => {
              const rParts = rowKeyParts[rowKey];
              // Compute grand totals per measure for this row
              const grandTotals = {};
              values.forEach(v => { grandTotals[v.id] = 0; });
              leafCombos.forEach(combo => {
                const ck = combo.join('\x00');
                values.forEach(v => {
                  grandTotals[v.id] += pivotMap[rowKey]?.[ck]?.[v.id] || 0;
                });
              });

              return (
                <tr key={`row-${rowIdx}`} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  {/* Row dimension cells */}
                  {rParts.map((part, partIdx) => (
                    <td
                      key={`row-${rowIdx}-dim-${partIdx}`}
                      className="border border-gray-300 px-3 py-2 font-medium text-gray-900 sticky left-0 bg-inherit whitespace-nowrap"
                      style={partIdx > 0 ? { left: partIdx * 120 } : {}}
                    >
                      {part || '(empty)'}
                    </td>
                  ))}

                  {/* Data cells for each leaf combo × each measure */}
                  {leafCombos.map((combo, comboIdx) => {
                    const ck = combo.join('\x00');
                    return values.map((val, valIdx) => {
                      const cellValue = pivotMap[rowKey]?.[ck]?.[val.id] || 0;
                      return (
                        <td
                          key={`cell-${rowIdx}-${comboIdx}-${valIdx}`}
                          className="border border-gray-300 px-3 py-2 text-right font-mono text-xs whitespace-nowrap"
                        >
                          {cellValue ? formatValue(cellValue, val.format) : <span className="text-gray-300">-</span>}
                        </td>
                      );
                    });
                  })}

                  {/* Grand total cells */}
                  {values.map((val, valIdx) => (
                    <td
                      key={`gt-${rowIdx}-${valIdx}`}
                      className="border border-gray-300 px-3 py-2 text-right font-mono text-xs font-semibold bg-blue-50 whitespace-nowrap"
                    >
                      {formatValue(grandTotals[val.id], val.format)}
                    </td>
                  ))}
                </tr>
              );
            })}

            {/* Column totals footer row */}
            <tr className="bg-gray-100 font-semibold">
              <td
                colSpan={rows.length}
                className="border border-gray-300 px-3 py-2 font-semibold text-gray-900 sticky left-0 bg-gray-100"
              >
                Total
              </td>
              {leafCombos.map((combo, comboIdx) => {
                const ck = combo.join('\x00');
                return values.map((val, valIdx) => {
                  const colTotal = sortedRowKeys.reduce((sum, rk) => {
                    return sum + (pivotMap[rk]?.[ck]?.[val.id] || 0);
                  }, 0);
                  return (
                    <td
                      key={`ft-${comboIdx}-${valIdx}`}
                      className="border border-gray-300 px-3 py-2 text-right font-mono text-xs whitespace-nowrap"
                    >
                      {formatValue(colTotal, val.format)}
                    </td>
                  );
                });
              })}
              {/* Grand total of grand totals */}
              {values.map((val, valIdx) => {
                const total = sortedRowKeys.reduce((sum, rk) => {
                  return sum + leafCombos.reduce((s, combo) => {
                    const ck = combo.join('\x00');
                    return s + (pivotMap[rk]?.[ck]?.[val.id] || 0);
                  }, 0);
                }, 0);
                return (
                  <td
                    key={`fgt-${valIdx}`}
                    className="border border-gray-300 px-3 py-2 text-right font-mono text-xs font-bold bg-blue-100 whitespace-nowrap"
                  >
                    {formatValue(total, val.format)}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  // Simple pivot table (no column dimensions - just rows and values)
  const renderSimplePivotTable = (data, rows, values) => {
    const pivotMap = {};
    const rowKeyParts = {};
    
    data.forEach(row => {
      const rParts = rows.map(r => {
        const v = row[r.id];
        return v !== null && v !== undefined ? String(v) : '';
      });
      const rowKey = rParts.join('\x00');
      
      if (!pivotMap[rowKey]) {
        pivotMap[rowKey] = {};
        rowKeyParts[rowKey] = rParts;
        values.forEach(v => { pivotMap[rowKey][v.id] = 0; });
      }
      values.forEach(v => {
        pivotMap[rowKey][v.id] += row[v.id] || 0;
      });
    });

    const sortedRowKeys = Object.keys(pivotMap).sort();

    return (
      <div className="overflow-auto max-h-[600px] border border-gray-200 rounded-lg">
        <table className="min-w-full border-collapse text-sm">
          <thead className="bg-gray-100 sticky top-0">
            <tr>
              {rows.map((r, idx) => (
                <th key={`sh-${idx}`} className="border border-gray-300 px-3 py-2 text-left font-semibold text-xs">
                  {r.name}
                </th>
              ))}
              {values.map((v, idx) => (
                <th key={`svh-${idx}`} className="border border-gray-300 px-3 py-2 text-right font-semibold text-xs">
                  {v.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRowKeys.map((rowKey, rowIdx) => (
              <tr key={`sr-${rowIdx}`} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                {rowKeyParts[rowKey].map((part, pIdx) => (
                  <td key={`sr-${rowIdx}-${pIdx}`} className="border border-gray-300 px-3 py-2 font-medium text-gray-900">
                    {part || '(empty)'}
                  </td>
                ))}
                {values.map((v, vIdx) => (
                  <td key={`sr-${rowIdx}-v${vIdx}`} className="border border-gray-300 px-3 py-2 text-right font-mono text-xs">
                    {formatValue(pivotMap[rowKey][v.id], v.format)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Add to useEffect
  useEffect(() => {
    loadAvailableFields();
    loadSavedCubes();
    loadSavedPivots();
  }, []);
  
  // Filter state
  const [filterConfig, setFilterConfig] = useState({
    dateRange: { start: '', end: '' },
    dimensionFilters: {},
    isDomesticTraffic: undefined,
    isFakeDlr: undefined,
    campaignId: ''
  });

  useEffect(() => {
    loadAvailableFields();
    loadSavedCubes();
  }, []);

  const loadAvailableFields = async () => {
    try {
      const data = await apiCallJson('/api/olap/dimensions');
      setAvailableDimensions(data.dimensions);
      setAvailableMeasures(data.measures);
    } catch (error) {
      console.error('Failed to load fields:', error);
      // Fallback to enhanced static data
    }
  };

  const loadSavedCubes = async () => {
    try {
      const cubes = await apiCallJson('/api/olap/cubes');
      setSavedCubes(cubes);
    } catch (error) {
      console.error('Failed to load cubes:', error);
    }
  };

  const addDimension = (dimension) => {
    if (!cubeConfig.dimensions.find(d => d.id === dimension.id)) {
      setCubeConfig(prev => ({
        ...prev,
        dimensions: [...prev.dimensions, dimension]
      }));
    }
  };

  const addMeasure = (measure) => {
    if (!cubeConfig.measures.find(m => m.id === measure.id)) {
      setCubeConfig(prev => ({
        ...prev,
        measures: [...prev.measures, measure]
      }));
    }
  };

  const removeDimension = (dimensionId) => {
    setCubeConfig(prev => ({
      ...prev,
      dimensions: prev.dimensions.filter(d => d.id !== dimensionId)
    }));
  };

  const removeMeasure = (measureId) => {
    setCubeConfig(prev => ({
      ...prev,
      measures: prev.measures.filter(m => m.id !== measureId)
    }));
  };

  const executeQuery = async () => {
    if (cubeConfig.dimensions.length === 0 || cubeConfig.measures.length === 0) {
      alert('Please select at least one dimension and one measure');
      return;
    }

    setLoading(true);
    try {
      const queryData = {
        dimensions: cubeConfig.dimensions.map(d => d.id),
        measures: cubeConfig.measures.map(m => m.id),
        filters: filterConfig,
        olapOperations,
        sortBy: cubeConfig.sortBy,
        sortOrder: cubeConfig.sortOrder,
        limit: cubeConfig.limit,
        host_tenant_id: user.host_tenant_id
      };

      const result = await apiCallJson('/api/olap/query', {
          method: 'POST', 
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify(queryData)
      });
      setQueryResult(result);
      setActiveTab('results');
      
      // Keep cube_id in URL when executing
      if (currentCubeId) {
        const newUrl = new URL(window.location);
        if (!newUrl.searchParams.has('cube_id')) {
          newUrl.searchParams.set('cube_id', currentCubeId);
          window.history.pushState({}, '', newUrl);
        }
      }
    } catch (error) {
      console.error('Query execution failed:', error);
      alert('Query execution failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const performDrillDown = async (dimension, value) => {
    const newPath = [...drillDownPath, { dimension: dimension.name || dimension, value }];
    setDrillDownPath(newPath);
    
    setOlapOperations(prev => ({
      ...prev,
      slice: [...prev.slice, { dimension: dimension.id || dimension, value }]
    }));
    
    await executeQuery();
  };

  const performDrillUp = async () => {
    if (drillDownPath.length > 0) {
      const newPath = drillDownPath.slice(0, -1);
      setDrillDownPath(newPath);
      
      setOlapOperations(prev => ({
        ...prev,
        slice: prev.slice.slice(0, -1)
      }));
      
      await executeQuery();
    }
  };

  const saveCube = async () => {
    if (!cubeConfig.name.trim()) {
      alert('Please enter a cube name');
      return;
    }

    try {
      const cubeData = {
        cube_name: cubeConfig.name,
        cube_config: {
          dimensions: cubeConfig.dimensions.map(d => d.id),
          measures: cubeConfig.measures.map(m => m.id),
          chartType: cubeConfig.chartType,
          filters: filterConfig,
          olapOperations,
          sortBy: cubeConfig.sortBy,
          sortOrder: cubeConfig.sortOrder,
          limit: cubeConfig.limit
        },
        is_shared: false
      };

      await apiCall('/api/olap/cubes', {
          method: 'POST', 
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify(cubeData)
      });
      await loadSavedCubes();
      setShowSaveDialog(false);
      alert('Cube saved successfully');
    } catch (error) {
      console.error('Failed to save cube:', error);
      alert('Failed to save cube: ' + error.message);
    }
  };

  const loadCube = (cube) => {
    const config = cube.cube_config;
    setCubeConfig({
      name: cube.cube_name,
      dimensions: (config.dimensions || []).map(dimId => 
        availableDimensions.find(d => d.id === dimId) || availableDimensions.find(d => d.id === dimId)
      ).filter(Boolean),
      measures: (config.measures || []).map(measId => 
        availableMeasures.find(m => m.id === measId) || availableMeasures.find(m => m.id === measId)
      ).filter(Boolean),
      chartType: config.chartType || 'bar',
      sortBy: config.sortBy || '',
      sortOrder: config.sortOrder || 'desc',
      limit: config.limit || 100
    });
    
    setFilterConfig(config.filters || { dateRange: { start: '', end: '' }, dimensionFilters: {}, isDomesticTraffic: undefined, isFakeDlr: undefined, campaignId: '' });
    setOlapOperations(config.olapOperations || { rollup: false, slice: [], dice: [], pivot: { rows: [], columns: [], values: [] } });
    setActiveTab('builder');
    
    // Clear pivot data when loading a cube
    setPivotData(null);
    setPivotConfig({ name: 'New Pivot Table', rows: [], columns: [], values: [], filters: [] });
    
    // Track current cube ID and update URL
    setCurrentCubeId(cube.id);
    setCurrentPivotId(null);
    const newUrl = new URL(window.location);
    newUrl.searchParams.set('cube_id', cube.id);
    newUrl.searchParams.delete('pivot_id');
    window.history.pushState({}, '', newUrl);
  };

  const resetCube = () => {
    setCubeConfig({
      name: 'New Cube',
      dimensions: [],
      measures: [],
      chartType: 'bar',
      sortBy: '',
      sortOrder: 'desc',
      limit: 100
    });
    setQueryResult(null);
    setFilterConfig({ dateRange: { start: '', end: '' }, dimensionFilters: {}, isDomesticTraffic: undefined, isFakeDlr: undefined, campaignId: '' });
    setOlapOperations({ rollup: false, slice: [], dice: [], pivot: { rows: [], columns: [], values: [] } });
    setDrillDownPath([]);
    
    // Clear URL parameters and current cube ID
    setCurrentCubeId(null);
    const newUrl = new URL(window.location);
    newUrl.searchParams.delete('cube_id');
    newUrl.searchParams.delete('pivot_id');
    window.history.pushState({}, '', newUrl);
  };

  const exportData = (format) => {
    if (!queryResult) return;
    
    const dataToExport = queryResult.data;
    
    if (format === 'csv') {
      const csv = [
        queryResult.columns.join(','),
        ...dataToExport.map(row => 
          queryResult.columns.map(col => row[col] || '').join(',')
        )
      ].join('\n');
      
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${cubeConfig.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } else if (format === 'json') {
      const json = JSON.stringify(dataToExport, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${cubeConfig.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const formatValue = (value, format) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(num)) {
      return typeof value === 'string' ? value : '-';
    }

    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
      case 'percentage':
        return `${num.toFixed(1)}%`;
      case 'number':
        return num.toLocaleString();
      case 'time':
        return `${num.toFixed(1)}s`;
      default:
        return num.toLocaleString();
    }
  };

  const renderChart = () => {
    if (!queryResult || !queryResult.data.length) return null;

    const data = queryResult.data;
    const xAxisKey = cubeConfig.dimensions[0]?.id || 'category';
    const yAxisKey = cubeConfig.measures[0]?.id || 'value';

    const chartProps = {
      data,
      onClick: (entry) => {
        if (entry && entry.activeLabel && cubeConfig.dimensions[0]) {
          performDrillDown(cubeConfig.dimensions[0], entry.activeLabel);
        }
      }
    };

    const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00c49f', '#ff8042', '#0088fe'];

    switch (cubeConfig.chartType) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart {...chartProps}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={xAxisKey} />
              <YAxis />
              <Tooltip formatter={(value, name) => {
                const measure = cubeConfig.measures.find(m => m.id === name);
                return [formatValue(value, measure?.format), measure?.name || name];
              }} />
              <Legend />
              {cubeConfig.measures.map((measure, index) => (
                <Line 
                  key={`line-${measure.id}`}
                  type="monotone" 
                  dataKey={measure.id} 
                  stroke={colors[index % colors.length]}
                  name={measure.name}
                  strokeWidth={2}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );
      
      case 'area':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart {...chartProps}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={xAxisKey} />
              <YAxis />
              <Tooltip formatter={(value, name) => {
                const measure = cubeConfig.measures.find(m => m.id === name);
                return [formatValue(value, measure?.format), measure?.name || name];
              }} />
              <Legend />
              {cubeConfig.measures.map((measure, index) => (
                <Area 
                  key={`area-${measure.id}`}
                  type="monotone" 
                  dataKey={measure.id} 
                  stackId="1"
                  stroke={colors[index % colors.length]}
                  fill={colors[index % colors.length]}
                  name={measure.name}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        );
      
      case 'scatter':
        if (cubeConfig.measures.length < 2) return <div className="text-center p-4">Scatter plot requires at least 2 measures</div>;
        return (
          <ResponsiveContainer width="100%" height={400}>
            <ScatterChart {...chartProps}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={cubeConfig.measures[0]?.id} name={cubeConfig.measures[0]?.name} />
              <YAxis dataKey={cubeConfig.measures[1]?.id} name={cubeConfig.measures[1]?.name} />
              <Tooltip formatter={(value, name) => {
                const measure = cubeConfig.measures.find(m => m.id === name);
                return [formatValue(value, measure?.format), measure?.name || name];
              }} />
              <Legend />
              <Scatter 
                name="Data Points" 
                dataKey={cubeConfig.measures[1]?.id} 
                fill={colors[0]} 
              />
            </ScatterChart>
          </ResponsiveContainer>
        );
      
      case 'pie':
        const pieData = data.slice(0, 8).map((item, index) => ({
          name: item[xAxisKey],
          value: item[yAxisKey]
        }));
        
        return (
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
                onClick={(entry) => {
                  if (cubeConfig.dimensions[0]) {
                    performDrillDown(cubeConfig.dimensions[0], entry.name);
                  }
                }}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => {
                const measure = cubeConfig.measures[0];
                return formatValue(value, measure?.format);
              }} />
            </PieChart>
          </ResponsiveContainer>
        );
      
      default: // bar chart
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart {...chartProps}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={xAxisKey} />
              <YAxis />
              <Tooltip formatter={(value, name) => {
                const measure = cubeConfig.measures.find(m => m.id === name);
                return [formatValue(value, measure?.format), measure?.name || name];
              }} />
              <Legend />
              {cubeConfig.measures.map((measure, index) => (
                <Bar 
                  key={`bar-${measure.id}`}
                  dataKey={measure.id} 
                  fill={colors[index % colors.length]}
                  name={measure.name}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );
    }
  };

  const renderPivotTable = () => {
    if (!queryResult) return null;

    const { rows, columns, values } = olapOperations.pivot;
    if (rows.length === 0 || values.length === 0) return null;

    // Reuse the enhanced pivot renderer by setting pivotData temporarily
    // Build a compatible structure
    const tempPivotConfig = { rows, columns, values };
    const tempData = queryResult.data;

    if (columns.length === 0) {
      // No column dimensions - simple table
      const pivotMap = {};
      const rowKeyParts = {};
      tempData.forEach(row => {
        const rParts = rows.map(r => String(row[r.id] ?? ''));
        const rowKey = rParts.join('\x00');
        if (!pivotMap[rowKey]) {
          pivotMap[rowKey] = {};
          rowKeyParts[rowKey] = rParts;
          values.forEach(v => { pivotMap[rowKey][v.id] = 0; });
        }
        values.forEach(v => {
          if (v.type === 'sum') pivotMap[rowKey][v.id] += row[v.id] || 0;
          else if (v.type === 'avg') pivotMap[rowKey][v.id] = (pivotMap[rowKey][v.id] + (row[v.id] || 0)) / 2;
          else pivotMap[rowKey][v.id] += row[v.id] || 0;
        });
      });
      const sortedRowKeys = Object.keys(pivotMap).sort();

      return (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-100">
                {rows.map((r, idx) => (
                  <th key={`rph-${idx}`} className="border border-gray-300 px-4 py-2 text-left">{r.name}</th>
                ))}
                {values.map((v, idx) => (
                  <th key={`vph-${idx}`} className="border border-gray-300 px-4 py-2 text-right">{v.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedRowKeys.map((rk, ri) => (
                <tr key={`rp-${ri}`} className="hover:bg-gray-50">
                  {rowKeyParts[rk].map((p, pi) => (
                    <td key={`rp-${ri}-${pi}`} className="border border-gray-300 px-4 py-2 font-medium">{p}</td>
                  ))}
                  {values.map((v, vi) => (
                    <td key={`rp-${ri}-v${vi}`} className="border border-gray-300 px-4 py-2 text-right">
                      {formatValue(pivotMap[rk][v.id], v.format)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // With column dimensions - build proper cross-tab
    const colDimValues = columns.map(col => {
      const seen = new Set();
      const ordered = [];
      tempData.forEach(row => {
        const v = String(row[col.id] ?? '');
        if (!seen.has(v)) { seen.add(v); ordered.push(v); }
      });
      return ordered;
    });

    const existingCombos = new Set();
    tempData.forEach(row => {
      existingCombos.add(columns.map(c => String(row[c.id] ?? '')).join('\x00'));
    });

    const leafCombos = [];
    const buildCombos = (depth, current) => {
      if (depth === columns.length) {
        if (existingCombos.has(current.join('\x00'))) leafCombos.push([...current]);
        return;
      }
      colDimValues[depth].forEach(val => { current.push(val); buildCombos(depth + 1, current); current.pop(); });
    };
    buildCombos(0, []);

    const pivotMap = {};
    const rowKeyParts = {};
    tempData.forEach(row => {
      const rParts = rows.map(r => String(row[r.id] ?? ''));
      const rowKey = rParts.join('\x00');
      const ck = columns.map(c => String(row[c.id] ?? '')).join('\x00');
      if (!pivotMap[rowKey]) { pivotMap[rowKey] = {}; rowKeyParts[rowKey] = rParts; }
      if (!pivotMap[rowKey][ck]) pivotMap[rowKey][ck] = {};
      values.forEach(v => {
        if (v.type === 'sum') pivotMap[rowKey][ck][v.id] = (pivotMap[rowKey][ck][v.id] || 0) + (row[v.id] || 0);
        else if (v.type === 'avg') pivotMap[rowKey][ck][v.id] = ((pivotMap[rowKey][ck][v.id] || 0) + (row[v.id] || 0)) / 2;
        else pivotMap[rowKey][ck][v.id] = (pivotMap[rowKey][ck][v.id] || 0) + (row[v.id] || 0);
      });
    });

    const sortedRowKeys = Object.keys(pivotMap).sort();
    const numValues = values.length;

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse border border-gray-300 text-sm">
          <thead>
            {/* Column dimension header */}
            <tr className="bg-gray-100">
              <th className="border border-gray-300 px-4 py-2 text-left" rowSpan={numValues > 1 ? 2 : 1} colSpan={rows.length}>
                {rows.map(r => r.name).join(' / ')}
              </th>
              {leafCombos.map((combo, idx) => (
                <th key={`ch-${idx}`} className="border border-gray-300 px-4 py-2 text-center bg-indigo-50" colSpan={numValues}>
                  {combo.join(' / ')}
                </th>
              ))}
            </tr>
            {/* Measure sub-headers */}
            {numValues > 1 && (
              <tr className="bg-gray-50">
                {leafCombos.map((combo, ci) =>
                  values.map((v, vi) => (
                    <th key={`mh-${ci}-${vi}`} className="border border-gray-300 px-3 py-1 text-center text-xs text-gray-600">
                      {v.name}
                    </th>
                  ))
                )}
              </tr>
            )}
          </thead>
          <tbody>
            {sortedRowKeys.map((rk, ri) => (
              <tr key={`r-${ri}`} className="hover:bg-gray-50">
                {rowKeyParts[rk].map((p, pi) => (
                  <td key={`r-${ri}-d${pi}`} className="border border-gray-300 px-4 py-2 font-medium">{p}</td>
                ))}
                {leafCombos.map((combo, ci) => {
                  const ck = combo.join('\x00');
                  return values.map((v, vi) => (
                    <td key={`c-${ri}-${ci}-${vi}`} className="border border-gray-300 px-4 py-2 text-right font-mono">
                      {formatValue(pivotMap[rk]?.[ck]?.[v.id] || 0, v.format)}
                    </td>
                  ));
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex justify-between">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">OLAP Cube Builder</h1>
          <div className="text-sm text-gray-500">
            {user?.username} - {cubeConfig.name}
          </div>
          {drillDownPath.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Path:</span>
              {drillDownPath.map((path, index) => (
                <span key={`path-${index}`} className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                  {path.dimension}: {path.value}
                </span>
              ))}
              <button
                onClick={performDrillUp}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                <ArrowUp className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
        <div className="mb-8 flex items-center space-x-2">
          <button
            onClick={() => setShowFilterDialog(true)}
            className="flex items-center space-x-1 px-3 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <Filter className="w-4 h-4" />
            <span>Filters</span>
          </button>
          <button
            onClick={() => setShowSaveDialog(true)}
            disabled={cubeConfig.dimensions.length === 0 || cubeConfig.measures.length === 0}
            className="flex items-center space-x-1 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span>Save</span>
          </button>
          <button
            onClick={resetCube}
            className="flex items-center space-x-1 px-3 py-2 text-gray-700 rounded-md hover:bg-gray-100"
          >
            <MoreVertical className="w-4 h-4" />
            <span>Reset</span>
          </button>
        </div>
      </div>

      <hr className="border-t border-gray-300" />

      {/* Main Content */}
      <div className="py-8">
        <div className="bg-white rounded-lg shadow p-2">
          <nav className="flex space-x-8">
            {[
              { id: 'builder', label: 'Cube Builder', icon: Grid3X3 },
              { id: 'saved', label: 'Saved Cubes', icon: Save },
              { id: 'results', label: 'Results & Charts', icon: BarChart3 },
              { id: 'pivot', label: 'Pivot Table', icon: MoreVertical }
            ].map(tab => (
              <button
                key={`tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      <div className="mx-auto">
        {activeTab === 'builder' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Available Fields */}
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Dimensions</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {availableDimensions.map(dimension => (
                    <button
                      key={`dim-${dimension.id}`}
                      onClick={() => addDimension(dimension)}
                      className="w-full p-3 text-left bg-gray-50 rounded-md hover:bg-gray-100 transition-colors"
                    >
                      <div className="font-medium text-sm">{dimension.name}</div>
                      <div className="text-xs text-gray-500">{dimension.type}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Measures</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {availableMeasures.map(measure => (
                    <button
                      key={`meas-${measure.id}`}
                      onClick={() => addMeasure(measure)}
                      className="w-full p-3 text-left bg-gray-50 rounded-md hover:bg-gray-100 transition-colors"
                    >
                      <div className="font-medium text-sm">{measure.name}</div>
                      <div className="text-xs text-gray-500">{measure.type} • {measure.format}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Cube Configuration */}
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Configuration</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cube Name</label>
                    <input
                      type="text"
                      value={cubeConfig.name}
                      onChange={(e) => setCubeConfig(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Chart Type</label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: 'bar', icon: BarChart3, label: 'Bar' },
                        { id: 'line', icon: TrendingUp, label: 'Line' },
                        { id: 'pie', icon: PieChartIcon, label: 'Pie' },
                        { id: 'area', icon: TrendingUp, label: 'Area' },
                        { id: 'scatter', icon: MoreVertical, label: 'Scatter' }
                      ].map(chart => (
                        <button
                          key={`chart-${chart.id}`}
                          onClick={() => setCubeConfig(prev => ({ ...prev, chartType: chart.id }))}
                          className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium ${
                            cubeConfig.chartType === chart.id
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          <chart.icon className="w-4 h-4" />
                          <span>{chart.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                      <select
                        value={cubeConfig.sortBy}
                        onChange={(e) => setCubeConfig(prev => ({ ...prev, sortBy: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">No sorting</option>
                        {[...cubeConfig.dimensions, ...cubeConfig.measures].map(field => (
                          <option key={`sort-${field.id}`} value={field.id}>{field.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Order</label>
                      <select
                        value={cubeConfig.sortOrder}
                        onChange={(e) => setCubeConfig(prev => ({ ...prev, sortOrder: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="desc">Descending</option>
                        <option value="asc">Ascending</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Limit Results</label>
                    <input
                      type="number"
                      value={cubeConfig.limit}
                      onChange={(e) => setCubeConfig(prev => ({ ...prev, limit: parseInt(e.target.value) || 100 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min="1"
                      max="10000"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">OLAP Operations</h3>
                <div className="space-y-3">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={olapOperations.rollup}
                      onChange={(e) => setOlapOperations(prev => ({ ...prev, rollup: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">Enable Rollup</span>
                 </label>

                 {olapOperations.slice.length > 0 && (
                   <div>
                     <div className="text-sm font-medium text-gray-700 mb-2">Active Slices</div>
                     {olapOperations.slice.map((slice, index) => (
                       <div key={`slice-${index}`} className="flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded text-sm">
                         <span>{slice.dimension} = {slice.value}</span>
                         <button
                           onClick={() => setOlapOperations(prev => ({
                             ...prev,
                             slice: prev.slice.filter((_, i) => i !== index)
                           }))}
                           className="text-red-500 hover:text-red-700"
                         >
                           <X className="w-4 h-4" />
                         </button>
                       </div>
                     ))}
                   </div>
                 )}
               </div>
             </div>

             <div className="bg-white rounded-lg shadow p-6">
               <h3 className="text-lg font-medium text-gray-900 mb-4">Selected Dimensions</h3>
               <div className="space-y-2 min-h-[100px]">
                 {cubeConfig.dimensions.length === 0 ? (
                   <div className="text-center text-gray-500 text-sm p-4 border-2 border-dashed border-gray-300 rounded-lg">
                     Click dimensions above to add them
                   </div>
                 ) : (
                   cubeConfig.dimensions.map((dimension, index) => (
                     <div key={`selected-dim-${dimension.id}-${index}`} className="flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded-md">
                       <span className="font-medium text-sm">{dimension.name}</span>
                       <button
                         onClick={() => removeDimension(dimension.id)}
                         className="text-red-500 hover:text-red-700"
                       >
                         <X className="w-4 h-4" />
                       </button>
                     </div>
                   ))
                 )}
               </div>
             </div>

             <div className="bg-white rounded-lg shadow p-6">
               <h3 className="text-lg font-medium text-gray-900 mb-4">Selected Measures</h3>
               <div className="space-y-2 min-h-[100px]">
                 {cubeConfig.measures.length === 0 ? (
                   <div className="text-center text-gray-500 text-sm p-4 border-2 border-dashed border-gray-300 rounded-lg">
                     Click measures above to add them
                   </div>
                 ) : (
                   cubeConfig.measures.map((measure, index) => (
                     <div key={`selected-meas-${measure.id}-${index}`} className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded-md">
                       <div>
                         <div className="font-medium text-sm">{measure.name}</div>
                         <div className="text-xs text-gray-500">{measure.type} • {measure.format}</div>
                       </div>
                       <button
                         onClick={() => removeMeasure(measure.id)}
                         className="text-red-500 hover:text-red-700"
                       >
                         <X className="w-4 h-4" />
                       </button>
                     </div>
                   ))
                 )}
               </div>
             </div>
           </div>

           {/* Actions */}
           <div className="space-y-6">
             <div className="bg-white rounded-lg shadow p-6">
               <h3 className="text-lg font-medium text-gray-900 mb-4">Actions</h3>
               <div className="space-y-3">
                 <button
                   onClick={executeQuery}
                   disabled={cubeConfig.dimensions.length === 0 || cubeConfig.measures.length === 0 || loading}
                   className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 disabled:opacity-50"
                 >
                   {loading ? (
                     <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                   ) : (
                     <>
                       <Eye className="w-4 h-4" />
                       <span>Execute Query</span>
                     </>
                   )}
                 </button>

                 {queryResult && (
                   <div className="space-y-2">
                     <div className="grid grid-cols-2 gap-2">
                       <button
                         onClick={() => exportData('csv')}
                         className="flex items-center justify-center space-x-1 px-3 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                       >
                         <Download className="w-4 h-4" />
                         <span>CSV</span>
                       </button>
                       <button
                         onClick={() => exportData('json')}
                         className="flex items-center justify-center space-x-1 px-3 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                       >
                         <Download className="w-4 h-4" />
                         <span>JSON</span>
                       </button>
                     </div>
                   </div>
                 )}
               </div>
             </div>

             {queryResult && (
               <div className="bg-white rounded-lg shadow p-6">
                 <h3 className="text-lg font-medium text-gray-900 mb-4">Query Summary</h3>
                 <div className="space-y-2 text-sm">
                   <div className="flex justify-between">
                     <span className="text-gray-600">Rows:</span>
                     <span className="font-medium">{queryResult.rowCount}</span>
                   </div>
                   <div className="flex justify-between">
                     <span className="text-gray-600">Total Rows:</span>
                     <span className="font-medium">{queryResult.totalRows}</span>
                   </div>
                   <div className="flex justify-between">
                     <span className="text-gray-600">Execution Time:</span>
                     <span className="font-medium">{queryResult.executionTime?.toFixed(0)}ms</span>
                   </div>
                   <div className="flex justify-between">
                     <span className="text-gray-600">Dimensions:</span>
                     <span className="font-medium">{cubeConfig.dimensions.length}</span>
                   </div>
                   <div className="flex justify-between">
                     <span className="text-gray-600">Measures:</span>
                     <span className="font-medium">{cubeConfig.measures.length}</span>
                   </div>
                   <div className="flex justify-between">
                     <span className="text-gray-600">Chart Type:</span>
                     <span className="font-medium capitalize">{cubeConfig.chartType}</span>
                   </div>
                 </div>
               </div>
             )}
           </div>
         </div>
       )}

       {activeTab === 'saved' && (
         <div className="bg-white rounded-lg shadow">
           <div className="px-6 py-4 border-b">
             <h3 className="text-lg font-medium text-gray-900">Saved OLAP Cubes</h3>
           </div>
           <div className="p-6">
             {savedCubes.length === 0 ? (
               <div className="text-center py-8">
                 <div className="text-gray-500">No saved cubes found</div>
                 <button
                   onClick={() => setActiveTab('builder')}
                   className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                 >
                   Create Your First Cube
                 </button>
               </div>
             ) : (
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                 {savedCubes.map((cube, index) => (
                   <div key={`cube-${cube.id || index}`} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                     <div className="flex items-start justify-between mb-3">
                       <h4 className="font-medium text-gray-900">{cube.cube_name}</h4>
                       <div className="flex space-x-1">
                         <button
                           onClick={() => loadCube(cube)}
                           className="text-blue-600 hover:text-blue-800 text-sm"
                         >
                           Load
                         </button>
                         <button
                           onClick={() => copyShareUrl('cube', cube.id, cube.cube_name)}
                           className="text-green-600 hover:text-green-800 text-sm"
                           title="Copy share link"
                         >
                           Share
                         </button>
                         <button
                           onClick={async () => {
                             if (window.confirm('Delete this cube?')) {
                               try {
                                 await apiCall(`/api/olap/cubes/${cube.id}`, {method: 'DELETE'});
                                 await loadSavedCubes();
                               } catch (error) {
                                 alert('Failed to delete cube');
                               }
                             }
                           }}
                           className="text-red-600 hover:text-red-800 text-sm"
                         >
                           Delete
                         </button>
                       </div>
                     </div>
                     <div className="space-y-1 text-sm text-gray-600">
                       <div>Dimensions: {cube.dimensions?.length || cube.cube_config?.dimensions?.length || 0}</div>
                       <div>Measures: {cube.measures?.length || cube.cube_config?.measures?.length || 0}</div>
                       <div>Chart: {cube.chartType || cube.cube_config?.chartType || 'bar'}</div>
                       <div>Created: {new Date(cube.created_at).toLocaleDateString()}</div>
                       <div>By: {cube.created_by}</div>
                       {cube.is_shared && (
                         <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                           Shared
                         </span>
                       )}
                     </div>
                   </div>
                 ))}
               </div>
             )}
           </div>
         </div>
       )}

       {activeTab === 'results' && (
         <div className="space-y-6">
           {/* Show regular cube results */}
           {queryResult && !pivotData ? (
             <>
               <div className="bg-white rounded-lg shadow p-6">
                 <div className="flex items-center justify-between mb-6">
                   <h3 className="text-lg font-medium text-gray-900">Visualization</h3>
                   <div className="flex space-x-2">
                     {[
                       { id: 'bar', icon: BarChart3, label: 'Bar' },
                       { id: 'line', icon: TrendingUp, label: 'Line' },
                       { id: 'pie', icon: PieChartIcon, label: 'Pie' },
                       { id: 'area', icon: TrendingUp, label: 'Area' },
                       { id: 'scatter', icon: MoreVertical, label: 'Scatter' }
                     ].map(chart => (
                       <button
                         key={`result-chart-${chart.id}`}
                         onClick={() => setCubeConfig(prev => ({ ...prev, chartType: chart.id }))}
                         className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium ${
                           cubeConfig.chartType === chart.id
                             ? 'bg-blue-100 text-blue-700'
                             : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                         }`}
                       >
                         <chart.icon className="w-4 h-4" />
                         <span>{chart.label}</span>
                       </button>
                     ))}
                   </div>
                 </div>
                 {renderChart()}
               </div>

               <div className="bg-white rounded-lg shadow">
                 <div className="px-6 py-4 border-b flex items-center justify-between">
                   <h3 className="text-lg font-medium text-gray-900">Data Table</h3>
                   <div className="text-sm text-gray-500">
                     Showing {queryResult.rowCount} of {queryResult.totalRows} rows
                   </div>
                 </div>
                 <div className="overflow-x-auto">
                   <table className="min-w-full divide-y divide-gray-200">
                     <thead className="bg-gray-50">
                       <tr>
                         {queryResult.columns.map((column, colIndex) => (
                           <th key={`col-header-${colIndex}-${column}`} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                             {availableDimensions.find(d => d.id === column)?.name || 
                              availableMeasures.find(m => m.id === column)?.name || 
                              column}
                           </th>
                         ))}
                       </tr>
                     </thead>
                     <tbody className="bg-white divide-y divide-gray-200">
                       {queryResult.data.map((row, rowIndex) => (
                         <tr key={`data-row-${rowIndex}`} className="hover:bg-gray-50">
                           {queryResult.columns.map((column, colIndex) => {
                             const dimension = availableDimensions.find(d => d.id === column);
                             const measure = availableMeasures.find(m => m.id === column);
                             const value = row[column];
                             return (
                               <td
                                 key={`data-cell-${rowIndex}-${colIndex}-${column}`}
                                 className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 cursor-pointer hover:bg-blue-50"
                                 onClick={() => {
                                   if (dimension && typeof value === 'string') {
                                     performDrillDown(dimension, value);
                                   }
                                 }}
                               >
                                 {measure ? formatValue(value, measure.format) : String(value || '')}
                               </td>
                             );
                           })}
                         </tr>
                       ))}
                     </tbody>
                   </table>
                 </div>
               </div>
             </>
           ) : !pivotData && !currentPivotId ? (
             <div className="bg-white rounded-lg shadow p-12">
               <div className="text-center">
                 <div className="text-gray-500 mb-4">No query results available</div>
                 <button
                   onClick={() => setActiveTab('builder')}
                   className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                 >
                   Go to Cube Builder
                 </button>
               </div>
             </div>
           ) : null}
           
           {/* Show pivot results in Results tab */}
           {pivotData && (
             <div className="bg-white rounded-lg shadow p-6">
               <div className="flex justify-between items-center mb-4">
                 <h3 className="text-lg font-medium text-gray-900">Pivot Table: {pivotConfig.name}</h3>
                 <div className="flex space-x-2">
                   <button
                     onClick={() => exportData('csv')}
                     className="flex items-center space-x-1 px-3 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                   >
                     <Download className="w-4 h-4" />
                     <span>Export CSV</span>
                   </button>
                   <button
                     onClick={generatePivotTable}
                     className="flex items-center space-x-1 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                   >
                     <Eye className="w-4 h-4" />
                     <span>Refresh</span>
                   </button>
                 </div>
               </div>
               {renderEnhancedPivotTable()}
               
               {/* Pivot Statistics */}
               <div className="mt-4 p-3 bg-gray-50 rounded-md">
                 <div className="flex justify-between text-sm text-gray-600">
                   <span>Rows: {Object.keys(pivotData.data.reduce((acc, row) => {
                     const key = pivotConfig.rows.map(r => row[r.id]).join('|');
                     acc[key] = true;
                     return acc;
                   }, {})).length}</span>
                   <span>Data Points: {pivotData.data.length}</span>
                   <span>Execution Time: {pivotData.executionTime?.toFixed(0)}ms</span>
                 </div>
               </div>
             </div>
           )}
         </div>
       )}

       {activeTab === 'pivot' && (
         <div className="space-y-6">
           {/* Pivot Configuration */}
           <div className="bg-white rounded-lg shadow p-6">
             <div className="flex justify-between items-center mb-6">
               <h3 className="text-lg font-medium text-gray-900">Pivot Table Builder</h3>
               <div className="flex space-x-2">
                 <button
                   onClick={() => setShowSavePivotDialog(true)}
                   disabled={pivotConfig.rows.length === 0 || pivotConfig.values.length === 0}
                   className="flex items-center space-x-1 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                 >
                   <Save className="w-4 h-4" />
                   <span>Save Pivot</span>
                 </button>
                 <button
                   onClick={() => {
                     setPivotConfig({ name: 'New Pivot Table', rows: [], columns: [], values: [], filters: [] });
                     setPivotData(null);
                     setCurrentPivotId(null);
                     const newUrl = new URL(window.location);
                     newUrl.searchParams.delete('cube_id');
                     newUrl.searchParams.delete('pivot_id');
                     window.history.pushState({}, '', newUrl);
                   }}
                   className="flex items-center space-x-1 px-3 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                 >
                   <X className="w-4 h-4" />
                   <span>Clear</span>
                 </button>
               </div>
             </div>

             {/* Available Fields for Dragging */}
             <div className="mb-6">
               <h4 className="text-sm font-medium text-gray-700 mb-3">Available Fields (Drag to zones below)</h4>
               <div className="grid grid-cols-2 gap-4">
                 <div>
                   <div className="text-xs font-medium text-gray-600 mb-2">DIMENSIONS</div>
                   <div className="flex flex-wrap gap-2">
                     {availableDimensions.map(dimension => (
                       <div
                         key={`drag-dim-${dimension.id}`}
                         draggable
                         onDragStart={(e) => handleDragStart(e, dimension, 'dimension')}
                         className="px-3 py-1 bg-blue-100 text-blue-800 rounded-md cursor-move hover:bg-blue-200 text-sm"
                       >
                         {dimension.name}
                       </div>
                     ))}
                   </div>
                 </div>
                 <div>
                   <div className="text-xs font-medium text-gray-600 mb-2">MEASURES</div>
                   <div className="flex flex-wrap gap-2">
                     {availableMeasures.map(measure => (
                       <div
                         key={`drag-meas-${measure.id}`}
                         draggable
                         onDragStart={(e) => handleDragStart(e, measure, 'measure')}
                         className="px-3 py-1 bg-green-100 text-green-800 rounded-md cursor-move hover:bg-green-200 text-sm"
                       >
                         {measure.name}
                       </div>
                     ))}
                   </div>
                 </div>
               </div>
             </div>

             {/* Pivot Configuration Zones */}
             <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
               {/* Filters Zone */}
               <div>
                 <h4 className="text-sm font-medium text-gray-700 mb-2">📋 Filters</h4>
                 <div
                   className="min-h-[120px] border-2 border-dashed border-gray-300 rounded-lg p-3 bg-gray-50"
                   onDragOver={handleDragOver}
                   onDrop={(e) => handleDrop(e, 'filters')}
                 >
                   {pivotConfig.filters.length === 0 ? (
                     <div className="text-center text-gray-500 text-sm py-8">Drop fields here to filter data</div>
                   ) : (
                     <div className="space-y-2">
                       {pivotConfig.filters.map((item, index) => (
                         <div key={`filter-${item.id}-${index}`} className="flex items-center justify-between p-2 bg-gray-100 border border-gray-300 rounded text-sm">
                           <span className="font-medium">{item.name}</span>
                           <button
                             onClick={() => removeFromPivot(item.id, 'filters')}
                             className="text-red-500 hover:text-red-700"
                           >
                             <X className="w-4 h-4" />
                           </button>
                         </div>
                       ))}
                     </div>
                   )}
                 </div>
               </div>

               {/* Rows Zone */}
               <div>
                 <h4 className="text-sm font-medium text-gray-700 mb-2">📊 Rows</h4>
                 <div
                   className="min-h-[120px] border-2 border-dashed border-yellow-300 rounded-lg p-3 bg-yellow-50"
                   onDragOver={handleDragOver}
                   onDrop={(e) => handleDrop(e, 'rows')}
                 >
                   {pivotConfig.rows.length === 0 ? (
                     <div className="text-center text-gray-500 text-sm py-8">Drop dimensions here for rows</div>
                   ) : (
                     <div className="space-y-2">
                       {pivotConfig.rows.map((item, index) => (
                         <div key={`row-${item.id}-${index}`} className="flex items-center justify-between p-2 bg-yellow-100 border border-yellow-300 rounded text-sm">
                           <span className="font-medium">{item.name}</span>
                           <button
                             onClick={() => removeFromPivot(item.id, 'rows')}
                             className="text-red-500 hover:text-red-700"
                           >
                             <X className="w-4 h-4" />
                           </button>
                         </div>
                       ))}
                     </div>
                   )}
                 </div>
               </div>

               {/* Columns Zone */}
               <div>
                 <h4 className="text-sm font-medium text-gray-700 mb-2">📈 Columns</h4>
                 <div
                   className="min-h-[120px] border-2 border-dashed border-purple-300 rounded-lg p-3 bg-purple-50"
                   onDragOver={handleDragOver}
                   onDrop={(e) => handleDrop(e, 'columns')}
                 >
                   {pivotConfig.columns.length === 0 ? (
                     <div className="text-center text-gray-500 text-sm py-8">Drop dimensions here for columns (optional)</div>
                   ) : (
                     <div className="space-y-2">
                       {pivotConfig.columns.map((item, index) => (
                         <div key={`col-${item.id}-${index}`} className="flex items-center justify-between p-2 bg-purple-100 border border-purple-300 rounded text-sm">
                           <span className="font-medium">{item.name}</span>
                           <button
                             onClick={() => removeFromPivot(item.id, 'columns')}
                             className="text-red-500 hover:text-red-700"
                           >
                             <X className="w-4 h-4" />
                           </button>
                         </div>
                       ))}
                     </div>
                   )}
                 </div>
               </div>

               {/* Values Zone */}
               <div>
                 <h4 className="text-sm font-medium text-gray-700 mb-2">💰 Values</h4>
                 <div
                   className="min-h-[120px] border-2 border-dashed border-green-300 rounded-lg p-3 bg-green-50"
                   onDragOver={handleDragOver}
                   onDrop={(e) => handleDrop(e, 'values')}
                 >
                   {pivotConfig.values.length === 0 ? (
                     <div className="text-center text-gray-500 text-sm py-8">Drop measures here for values</div>
                   ) : (
                     <div className="space-y-2">
                       {pivotConfig.values.map((item, index) => (
                         <div key={`val-${item.id}-${index}`} className="flex items-center justify-between p-2 bg-green-100 border border-green-300 rounded text-sm">
                           <div>
                             <div className="font-medium">{item.name}</div>
                             <div className="text-xs text-gray-500">{item.type} • {item.format}</div>
                           </div>
                           <button
                             onClick={() => removeFromPivot(item.id, 'values')}
                             className="text-red-500 hover:text-red-700"
                           >
                             <X className="w-4 h-4" />
                           </button>
                         </div>
                       ))}
                     </div>
                   )}
                 </div>
               </div>
             </div>

             {/* Pivot Controls */}
             <div className="flex items-center justify-between pt-4 border-t">
               <div className="flex items-center space-x-4">
                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">Pivot Name</label>
                   <input
                     type="text"
                     value={pivotConfig.name}
                     onChange={(e) => setPivotConfig(prev => ({ ...prev, name: e.target.value }))}
                     className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                     placeholder="Enter pivot name"
                   />
                 </div>
               </div>
               
               <div className="flex space-x-3">
                 <button
                   onClick={generatePivotTable}
                   disabled={pivotConfig.rows.length === 0 || pivotConfig.values.length === 0 || loading}
                   className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                 >
                   {loading ? (
                     <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                   ) : (
                     <Eye className="w-4 h-4" />
                   )}
                   <span>Generate Pivot</span>
                 </button>
               </div>
             </div>

             {/* Configuration Summary */}
             {(pivotConfig.rows.length > 0 || pivotConfig.columns.length > 0 || pivotConfig.values.length > 0) && (
               <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                 <h5 className="text-sm font-medium text-blue-900 mb-2">Configuration Summary:</h5>
                 <div className="grid grid-cols-2 gap-4 text-sm">
                   <div>
                     {pivotConfig.filters.length > 0 && (
                       <div><strong>Filters:</strong> {pivotConfig.filters.map(f => f.name).join(', ')}</div>
                     )}
                     {pivotConfig.rows.length > 0 && (
                       <div><strong>Rows:</strong> {pivotConfig.rows.map(r => r.name).join(', ')}</div>
                     )}
                   </div>
                   <div>
                     {pivotConfig.columns.length > 0 && (
                       <div><strong>Columns:</strong> {pivotConfig.columns.map(c => c.name).join(', ')}</div>
                     )}
                     {pivotConfig.values.length > 0 && (
                       <div><strong>Values:</strong> {pivotConfig.values.map(v => v.name).join(', ')}</div>
                     )}
                   </div>
                 </div>
               </div>
             )}
           </div>

           {/* Saved Pivots */}
           <div className="bg-white rounded-lg shadow p-6">
             <h3 className="text-lg font-medium text-gray-900 mb-4">Saved Pivot Tables</h3>
             {savedPivots.length === 0 ? (
               <div className="text-center py-4 text-gray-500">No saved pivot tables found</div>
             ) : (
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                 {savedPivots.map((pivot, index) => (
                   <div key={`saved-pivot-${pivot.id || index}`} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                     <div className="flex items-start justify-between mb-3">
                       <h4 className="font-medium text-gray-900">{pivot.pivot_name}</h4>
                       <div className="flex space-x-1">
                         <button
                           onClick={() => loadPivot(pivot)}
                           className="text-blue-600 hover:text-blue-800 text-sm"
                         >
                           Load
                         </button>
                         <button
                           onClick={() => copyShareUrl('pivot', pivot.id, pivot.pivot_name)}
                           className="text-green-600 hover:text-green-800 text-sm"
                           title="Copy share link"
                         >
                           Share
                         </button>
                         <button
                           onClick={async () => {
                             if (window.confirm('Delete this pivot?')) {
                               try {
                                 await apiCall(`/api/olap/pivots/${pivot.id}`, {method: 'DELETE'});
                                 await loadSavedPivots();
                               } catch (error) {
                                 alert('Failed to delete pivot');
                               }
                             }
                           }}
                           className="text-red-600 hover:text-red-800 text-sm"
                         >
                           Delete
                         </button>
                       </div>
                     </div>
                     <div className="space-y-1 text-sm text-gray-600">
                       <div>Rows: {pivot.pivot_config?.rows?.length || 0}</div>
                       <div>Columns: {pivot.pivot_config?.columns?.length || 0}</div>
                       <div>Values: {pivot.pivot_config?.values?.length || 0}</div>
                       <div>Created: {new Date(pivot.created_at).toLocaleDateString()}</div>
                       <div>By: {pivot.created_by}</div>
                     </div>
                   </div>
                 ))}
               </div>
             )}
           </div>

           {/* Pivot Table Results */}
           {pivotData && (
             <div className="bg-white rounded-lg shadow p-6">
               <div className="flex justify-between items-center mb-4">
                 <h3 className="text-lg font-medium text-gray-900">Pivot Table Results</h3>
                 <div className="flex space-x-2">
                   <button
                     onClick={() => exportData('csv')}
                     className="flex items-center space-x-1 px-3 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                   >
                     <Download className="w-4 h-4" />
                     <span>Export CSV</span>
                   </button>
                 </div>
               </div>
               {renderEnhancedPivotTable()}
               
               {/* Pivot Statistics */}
               {pivotData && (
                 <div className="mt-4 p-3 bg-gray-50 rounded-md">
                   <div className="flex justify-between text-sm text-gray-600">
                     <span>Rows: {Object.keys(pivotData.data.reduce((acc, row) => {
                       const key = pivotConfig.rows.map(r => row[r.id]).join('|');
                       acc[key] = true;
                       return acc;
                     }, {})).length}</span>
                     <span>Data Points: {pivotData.data.length}</span>
                     <span>Execution Time: {pivotData.executionTime?.toFixed(0)}ms</span>
                   </div>
                 </div>
               )}
             </div>
           )}
         </div>
       )}
     </div>

     {/* Save Dialog */}
     {showSaveDialog && (
       <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
         <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white max-w-lg">
           <div className="mt-3">
             <div className="flex items-center justify-between mb-4">
               <h3 className="text-lg font-medium text-gray-900">Save OLAP Cube</h3>
               <button
                 onClick={() => setShowSaveDialog(false)}
                 className="text-gray-400 hover:text-gray-600"
               >
                 <X className="w-5 h-5" />
               </button>
             </div>
             
             <div className="space-y-4">
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-2">
                   Cube Name
                 </label>
                 <input
                   type="text"
                   value={cubeConfig.name}
                   onChange={(e) => setCubeConfig(prev => ({ ...prev, name: e.target.value }))}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                   placeholder="Enter cube name"
                 />
               </div>
               
               <div className="text-sm text-gray-600">
                 <div className="mb-2">
                   <strong>Dimensions:</strong> {cubeConfig.dimensions.map(d => d.name).join(', ')}
                 </div>
                 <div className="mb-2">
                   <strong>Measures:</strong> {cubeConfig.measures.map(m => m.name).join(', ')}
                 </div>
                 <div className="mb-2">
                   <strong>Chart Type:</strong> {cubeConfig.chartType}
                 </div>
                 {Object.keys(filterConfig.dimensionFilters).length > 0 && (
                   <div className="mb-2">
                     <strong>Active Filters:</strong> {Object.keys(filterConfig.dimensionFilters).length}
                   </div>
                 )}
               </div>
               
               <div className="flex space-x-3 pt-4">
                 <button
                   onClick={saveCube}
                   className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                 >
                   Save Cube
                 </button>
                 <button
                   onClick={() => setShowSaveDialog(false)}
                   className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                 >
                   Cancel
                 </button>
               </div>
             </div>
           </div>
         </div>
       </div>
     )}

     {/* Filter Dialog */}
     {showFilterDialog && (
       <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
         <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white max-w-2xl">
           <div className="mt-3">
             <div className="flex items-center justify-between mb-6">
               <h3 className="text-lg font-medium text-gray-900">Advanced Filters</h3>
               <button
                 onClick={() => setShowFilterDialog(false)}
                 className="text-gray-400 hover:text-gray-600"
               >
                 <X className="w-5 h-5" />
               </button>
             </div>

             <div className="space-y-6">
               <div>
                 <h4 className="text-md font-medium text-gray-900 mb-3">Date Range</h4>
                 <div className="grid grid-cols-2 gap-4">
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                     <input
                       type="date"
                       value={filterConfig.dateRange.start}
                       onChange={(e) => setFilterConfig(prev => ({
                         ...prev,
                         dateRange: { ...prev.dateRange, start: e.target.value }
                       }))}
                       className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                     />
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                     <input
                       type="date"
                       value={filterConfig.dateRange.end}
                       onChange={(e) => setFilterConfig(prev => ({
                         ...prev,
                         dateRange: { ...prev.dateRange, end: e.target.value }
                       }))}
                       className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                     />
                   </div>
                 </div>
               </div>

               <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">Boolean Filters</h4>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={filterConfig.isDomesticTraffic === true}
                      onChange={(e) => setFilterConfig(prev => ({
                        ...prev,
                        isDomesticTraffic: e.target.checked ? true : undefined
                      }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">Domestic Traffic Only</span>
                  </label>
                  
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={filterConfig.isFakeDlr === false}
                      onChange={(e) => setFilterConfig(prev => ({
                        ...prev,
                        isFakeDlr: e.target.checked ? false : undefined
                      }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">Exclude Fake DLR</span>
                  </label>
                </div>
              </div>

              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">Campaign Filter</h4>
                <input
                  type="text"
                  placeholder="Campaign ID"
                  value={filterConfig.campaignId || ''}
                  onChange={(e) => setFilterConfig(prev => ({
                    ...prev,
                    campaignId: e.target.value
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

               <div className="flex space-x-3 pt-6 border-t">
                 <button
                   onClick={() => {
                     setShowFilterDialog(false);
                     executeQuery();
                   }}
                   className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                 >
                   Apply Filters
                 </button>
                 <button
                   onClick={() => {
                     setFilterConfig({ dateRange: { start: '', end: '' }, dimensionFilters: {}, isDomesticTraffic: undefined, isFakeDlr: undefined, campaignId: '' });
                     setShowFilterDialog(false);
                   }}
                   className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                 >
                   Clear All
                 </button>
               </div>
             </div>
           </div>
         </div>
       </div>
     )}

     {/* Save Pivot Dialog */}
     {showSavePivotDialog && (
       <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
         <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white max-w-lg">
           <div className="mt-3">
             <div className="flex items-center justify-between mb-4">
               <h3 className="text-lg font-medium text-gray-900">Save Pivot Table</h3>
               <button
                 onClick={() => setShowSavePivotDialog(false)}
                 className="text-gray-400 hover:text-gray-600"
               >
                 <X className="w-5 h-5" />
               </button>
             </div>
             
             <div className="space-y-4">
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-2">
                   Pivot Name
                 </label>
                 <input
                   type="text"
                   value={pivotConfig.name}
                   onChange={(e) => setPivotConfig(prev => ({ ...prev, name: e.target.value }))}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                   placeholder="Enter pivot name"
                 />
               </div>
               
               <div className="text-sm text-gray-600">
                 <div className="mb-2">
                   <strong>Rows:</strong> {pivotConfig.rows.map(r => r.name).join(', ') || 'None'}
                 </div>
                 <div className="mb-2">
                   <strong>Columns:</strong> {pivotConfig.columns.map(c => c.name).join(', ') || 'None'}
                 </div>
                 <div className="mb-2">
                   <strong>Values:</strong> {pivotConfig.values.map(v => v.name).join(', ') || 'None'}
                 </div>
                 {pivotConfig.filters.length > 0 && (
                   <div className="mb-2">
                     <strong>Filters:</strong> {pivotConfig.filters.map(f => f.name).join(', ')}
                   </div>
                 )}
               </div>
               
               <div className="flex space-x-3 pt-4">
                 <button
                   onClick={savePivot}
                   className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                 >
                   Save Pivot
                 </button>
                 <button
                   onClick={() => setShowSavePivotDialog(false)}
                   className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                 >
                   Cancel
                 </button>
               </div>
             </div>
           </div>
         </div>
       </div>
     )}

     {activeTab === 'results' && (
      <div className="fixed bottom-4 right-4 z-40">
        <SchedulerWidget isVisible={true} position="static"/>
      </div>
     )}
   </div>
 );
};

export default OLAPCubeBuilder;